import React from 'react';
import { Dropdown, Text } from '@vibe/core';
import type { MondayColumn } from '../../api/board-config.api';

// ─── Props ────────────────────────────────────────────────────────────────────

interface FieldMappingRowProps {
  /** Concept name displayed as label (e.g. "Scheduled Hours") */
  label: string;
  /** Description of what this field maps to */
  description?: string;
  /** Expected column type (e.g. "Numbers", "People", "Timeline") */
  expectedType: string;
  /** Currently selected column ID (null if unmapped) */
  selectedColumnId: string | null;
  /** Available columns to choose from */
  columns: MondayColumn[];
  /** Called when the user selects a column */
  onChange: (columnId: string | null) => void;
  /** When true, show all columns without type filtering or mismatch warnings */
  skipTypeValidation?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * A single field mapping row showing a concept label and a dropdown
 * to select which monday.com column maps to that concept.
 */
/** Check whether a monday.com column type matches the expected concept type */
export function matchesExpectedType(colType: string, expectedType: string): boolean {
  return colType.toLowerCase() === expectedType.toLowerCase().replace(' ', '_');
}

const FieldMappingRow: React.FC<FieldMappingRowProps> = ({
  label,
  description,
  expectedType,
  selectedColumnId,
  columns,
  onChange,
  skipTypeValidation,
}) => {
  // When type validation is skipped, show all columns; otherwise prioritise matching type
  const matchingCols = skipTypeValidation
    ? columns
    : columns.filter((col) => matchesExpectedType(col.type, expectedType));
  const otherCols = skipTypeValidation
    ? []
    : columns.filter((col) => !matchesExpectedType(col.type, expectedType));

  const options = [
    { value: '__none__', label: '-- None --' },
    ...matchingCols.map((col) => ({
      value: col.id,
      label: skipTypeValidation ? `${col.title} (${col.type})` : col.title,
    })),
    ...otherCols.map((col) => ({
      value: col.id,
      label: `${col.title} (${col.type})`,
    })),
  ];

  const selectedOption = options.find(
    (opt) => opt.value === (selectedColumnId ?? '__none__'),
  );

  // Detect type mismatch for the currently selected column (skip if validation disabled)
  const selectedColumn = selectedColumnId
    ? columns.find((col) => col.id === selectedColumnId)
    : null;
  const hasTypeMismatch = !skipTypeValidation && selectedColumn
    ? !matchesExpectedType(selectedColumn.type, expectedType)
    : false;

  return (
    <div className="rp-field-mapping-row">
      <div className="rp-field-mapping-row__label">
        <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM}>
          {label}
        </Text>
        <span className="rp-field-mapping-row__type-badge">{expectedType}</span>
      </div>
      <div className="rp-field-mapping-row__dropdown">
        <Dropdown
          size={Dropdown.sizes.SMALL}
          options={options}
          value={selectedOption}
          onChange={(option) => {
            if (!option) {
              onChange(null);
              return;
            }
            const selected = option as { value: string; label: string };
            onChange(selected.value === '__none__' ? null : selected.value);
          }}
          placeholder="Select column..."
          insideOverflowContainer
          className="rp-field-mapping-row__select"
        />
        {hasTypeMismatch && (
          <div className="rp-field-mapping-row__warning">
            ⚠ Expected {expectedType} column, got {selectedColumn!.type}
          </div>
        )}
      </div>
    </div>
  );
};

export default FieldMappingRow;
